import React, { useState } from 'react';
import {
  FiAlertCircle,
  FiExternalLink,
  FiShield,
  FiKey,
  FiWifi,
  FiSettings,
} from 'react-icons/fi';

interface ParsedError {
  type: 'permission' | 'credentials' | 'config' | 'network' | 'unknown';
  title: string;
  message: string;
  guidance: string[];
  docsLink?: string;
  policyExample?: string;
}

/**
 * Parse AWS/Bedrock errors and return user-friendly messages with guidance
 */
export const parseBedrockError = (errorMessage: string): ParsedError => {
  const error = errorMessage.toLowerCase();

  if (error.includes('accessdeniedexception') || error.includes('not authorized') || error.includes('identity-based policy')) {
    const actionMatch = errorMessage.match(/bedrock:(\w+)/);
    const action = actionMatch ? actionMatch[1] : 'access Bedrock';

    return {
      type: 'permission',
      title: 'AWS Permission Required',
      message: `Your AWS credentials don't have permission to ${action}.`,
      guidance: [
        'Add Bedrock permissions to your IAM user/role',
        'Required actions: bedrock:ListPrompts, bedrock:GetPrompt, bedrock:CreatePrompt',
        'Or use an IAM role with the AmazonBedrockFullAccess managed policy',
      ],
      docsLink: 'https://docs.aws.amazon.com/bedrock/latest/userguide/security-iam.html',
      policyExample: `{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Action": [
      "bedrock:ListPrompts",
      "bedrock:GetPrompt",
      "bedrock:CreatePrompt",
      "bedrock:UpdatePrompt",
      "bedrock:DeletePrompt",
      "bedrock:CreatePromptVersion"
    ],
    "Resource": "*"
  }]
}`,
    };
  }

  if (error.includes('credentials') || error.includes('no credentials') || error.includes('unable to locate credentials')) {
    return {
      type: 'credentials',
      title: 'AWS Credentials Not Found',
      message: 'No AWS credentials are configured for the backend service.',
      guidance: [
        'Configure AWS credentials in your environment',
        'Set AWS_ACCESS_KEY_ID and AWS_SECRET_ACCESS_KEY environment variables',
        'Or configure AWS SSO/IAM role for the service',
      ],
      docsLink: 'https://docs.aws.amazon.com/cli/latest/userguide/cli-configure-files.html',
    };
  }

  if (error.includes('region')) {
    return {
      type: 'config',
      title: 'AWS Region Not Configured',
      message: 'AWS region is not set or Bedrock is not available in the configured region.',
      guidance: [
        'Set AWS_REGION environment variable (e.g., us-east-1)',
        'Bedrock Prompt Management is available in: us-east-1, us-west-2, eu-west-1',
      ],
    };
  }

  if (error.includes('timeout') || error.includes('network') || error.includes('connection')) {
    return {
      type: 'network',
      title: 'Connection Error',
      message: 'Unable to connect to AWS Bedrock service.',
      guidance: [
        'Check your network connection',
        'Verify AWS service is accessible from your environment',
        'Check if there are any firewall rules blocking the connection',
      ],
    };
  }

  if (error.includes('model access') || error.includes('not enabled')) {
    return {
      type: 'config',
      title: 'Bedrock Not Enabled',
      message: 'AWS Bedrock Prompt Management may not be enabled in your account.',
      guidance: [
        'Enable Bedrock in your AWS account through the AWS Console',
        'Request access to Bedrock models if needed',
        'Verify the service is available in your region',
      ],
      docsLink: 'https://docs.aws.amazon.com/bedrock/latest/userguide/setting-up.html',
    };
  }

  return {
    type: 'unknown',
    title: 'Bedrock API Error',
    message: errorMessage,
    guidance: [
      'Check the error message for details',
      'Verify your AWS configuration is correct',
      'Contact support if the issue persists',
    ],
  };
};

interface BedrockErrorAlertProps {
  error: string;
  compact?: boolean;
}

/**
 * BedrockErrorAlert Component
 * Displays user-friendly error messages with guidance for AWS/Bedrock errors
 */
const BedrockErrorAlert: React.FC<BedrockErrorAlertProps> = ({ error, compact = false }) => {
  const [showPolicy, setShowPolicy] = useState(false);
  const parsedError = parseBedrockError(error);

  const getIcon = () => {
    switch (parsedError.type) {
      case 'permission':
        return <FiShield className="w-5 h-5" />;
      case 'credentials':
        return <FiKey className="w-5 h-5" />;
      case 'network':
        return <FiWifi className="w-5 h-5" />;
      case 'config':
        return <FiSettings className="w-5 h-5" />;
      default:
        return <FiAlertCircle className="w-5 h-5" />;
    }
  };

  const getBgColor = () => {
    switch (parsedError.type) {
      case 'permission':
        return 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800';
      case 'credentials':
        return 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800';
      case 'network':
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
      case 'config':
        return 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800';
      default:
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
    }
  };

  const getTextColor = () => {
    switch (parsedError.type) {
      case 'permission':
        return 'text-amber-800 dark:text-amber-300';
      case 'credentials':
        return 'text-orange-800 dark:text-orange-300';
      case 'network':
        return 'text-blue-800 dark:text-blue-300';
      case 'config':
        return 'text-purple-800 dark:text-purple-300';
      default:
        return 'text-red-700 dark:text-red-400';
    }
  };

  if (compact) {
    return (
      <div className={`p-3 rounded-lg border ${getBgColor()}`}>
        <div className={`flex items-start gap-2 ${getTextColor()}`}>
          <div className="flex-shrink-0 mt-0.5">{getIcon()}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">{parsedError.title}</p>
            <p className="text-xs mt-0.5 opacity-90">{parsedError.message}</p>
            {parsedError.docsLink && (
              <a
                href={parsedError.docsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs mt-1 hover:underline"
              >
                <FiExternalLink className="w-3 h-3" />
                Learn more
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`mb-4 p-4 rounded-lg border ${getBgColor()}`}>
      <div className={`flex items-start gap-3 ${getTextColor()}`}>
        <div className="flex-shrink-0 mt-0.5">{getIcon()}</div>
        <div className="flex-1">
          <h4 className="font-semibold">{parsedError.title}</h4>
          <p className="text-sm mt-1 opacity-90">{parsedError.message}</p>
        </div>
      </div>

      {parsedError.guidance.length > 0 && (
        <div className="mt-3 ml-8">
          <p className={`text-xs font-medium uppercase tracking-wide mb-2 ${getTextColor()} opacity-70`}>
            How to fix this:
          </p>
          <ul className={`text-sm space-y-1 ${getTextColor()} opacity-90`}>
            {parsedError.guidance.map((item) => (
              <li key={item} className="flex items-start gap-2">
                <span className="text-xs mt-1">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {parsedError.policyExample && (
        <div className="mt-3 ml-8">
          <button
            onClick={() => setShowPolicy(!showPolicy)}
            className={`text-sm font-medium underline ${getTextColor()} hover:opacity-80`}
          >
            {showPolicy ? 'Hide' : 'Show'} example IAM policy
          </button>
          {showPolicy && (
            <pre className="mt-2 p-3 bg-gray-900 text-gray-100 text-xs rounded-lg overflow-x-auto">
              <code>{parsedError.policyExample}</code>
            </pre>
          )}
        </div>
      )}

      {parsedError.docsLink && (
        <div className="mt-3 ml-8">
          <a
            href={parsedError.docsLink}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1 text-sm font-medium ${getTextColor()} hover:underline`}
          >
            <FiExternalLink className="w-3.5 h-3.5" />
            View AWS documentation
          </a>
        </div>
      )}
    </div>
  );
};

export default BedrockErrorAlert;
